import { Icon } from "@pepperstone/pepper-ui-kit";
import { ChangeEvent, useId } from "react";

import { FieldError } from "../FieldError";
import { Typo } from "../Typo";

type SelectPropsBase = {
  className?: string;
  placeholder: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  label?: string;
  subLabel?: string;
  error?: string;
};

type SelectPropsChildren = {
  options?: never;
  children: React.ReactNode;
};

type SelectPropsOptions = {
  options: [string, string][];
  children?: never;
};

export type SelectProps = SelectPropsBase &
  (SelectPropsChildren | SelectPropsOptions);

export const Select = ({
  className,
  placeholder,
  name,
  value,
  options,
  children,
  onChange,
  error,
  subLabel,
  label,
}: SelectProps) => {
  const id = useId();

  const render = () => {
    if (options !== undefined) {
      return options.map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ));
    }

    return children;
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label htmlFor={id} className="flex flex-col gap-1">
          <Typo element="p" type="headingMD">
            {label}
          </Typo>

          {subLabel && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {subLabel}
            </Typo>
          )}
        </label>
      )}
      <div className="relative">
        <select
          className={`${value === "" ? "text-gray-500" : "text-inherit"} w-full appearance-none truncate rounded-md bg-cta-background-secondary-base p-2 pr-6 outline outline-1 hover:outline-border-blue-base ${error ? "outline-red-700" : "outline-gray-200"}`}
          value={value}
          onChange={onChange}
          id={id}
          name={name}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {render()}
        </select>
        <div className="pointer-events-none absolute inset-y-0 end-3 top-3">
          <Icon icon="arrow-down" size="tiny" />
        </div>
      </div>
      {error && <FieldError text={error} />}
    </div>
  );
};
